import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import { base64ToArrayBuffer } from '../../components/common/DocumentUpload';
import { setProgress } from './uploadProgressSlice';
import { openMessage } from './showMessageSlice';
import { openSessionExpiredDialog } from './applicationSlice';
import { clearAuth, updateAccessTokens } from './authSlice';
import { RETURN_URL_STORAGE_KEY, isValidReturnPath } from '../../util/returnUrl';

// ============================================================================
// ENVIRONMENT CONFIGURATION FLAG
// ============================================================================
// 
// ⚙️ SINGLE FLAG TO SWITCH BETWEEN LOCAL AND DEPLOYED SETUPS
// 
// Set USE_LOCAL_MICROSERVICE_SETUP to:
// 
//   true  → LOCAL DEVELOPMENT MODE
//           - Routes requests to individual microservice URLs (e.g., localhost:3002, localhost:3003)
//           - Uses dynamicBaseQuery with service-specific routing
//           - Enables detailed request logging
//           - Example: http://localhost:3002/trial-mgmt/auth/login
// 
//   false → DEPLOYED/PRODUCTION MODE
//           - Routes all requests through single API endpoint
//           - Uses baseQueryWithReauth with unified routing
//           - Example: https://api.dev.bloqcube.com/trial-mgmt/auth/login
// 
// This flag automatically configures:
//   ✓ Base query selection (baseQueryWithReauthLocal vs baseQueryWithReauth)
//   ✓ URL formatting (getServiceBaseUrl, formatServicePath)
//   ✓ Axios URL building (getAxiosUrl)
//   ✓ Request logging behavior
// 
// ============================================================================
const USE_LOCAL_MICROSERVICE_SETUP = false; // 👈 Change ONLY this flag to switch environments
// ============================================================================

const env = process.env.NODE_ENV;
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';

/**
 * Configuration for microservices including development and production settings
 */
export const MICROSERVICE_CONFIG = {
  'trial-mgmt': {
    devUrl: process.env.REACT_APP_TRIAL_MGMT_URL || 'http://localhost:3002/trial-mgmt',
    basePath: 'trial-mgmt',
    port: 3002
  },
  'form-library': {
    devUrl: process.env.REACT_APP_FORM_LIBRARY_URL || 'http://localhost:3003/form-library',
    basePath: 'form-library',
    port: 3002
  },
  'e-consent': {
    devUrl: process.env.REACT_APP_E_CONSENT_URL || 'http://localhost:3004/e-consent',
    basePath: 'e-consent',
    port: 3003
  },
  'audit-log': {
    devUrl: process.env.REACT_APP_AUDIT_LOG_URL || 'http://localhost:3007/audit-log',
    basePath: 'audit-log',
    port: 3004
  },
  'notification': {
    devUrl: process.env.REACT_APP_NOTIFICATION_URL || 'http://localhost:3013/notification',
    basePath: 'notification',
    port: 3013
  },
  'finance-service': {
    devUrl: process.env.REACT_APP_FINANCE_SERVICE_URL || 'http://localhost:3006/finance-service',
    basePath: 'finance-service',
    port: 3006
  },
};

// Default API endpoint
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT_URL || '';

/**
 * Helper function to extract service name from a URL path
 */
const extractServiceName = (url) => {
  if (!url) return null;
  
  const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
  const parts = cleanUrl.split('/');
  return parts[0] || null;
};

/**
 * Helper function to get the base URL for a service
 */
const getServiceBaseUrl = (serviceName) => {
  if (!serviceName || !MICROSERVICE_CONFIG[serviceName]) {
    return API_ENDPOINT;
  }
  
  if (USE_LOCAL_MICROSERVICE_SETUP) {
    return MICROSERVICE_CONFIG[serviceName].devUrl;
  }
  
  return API_ENDPOINT;
};

/**
 * Helper function to format the path for a service
 */
export const formatServicePath = (url, serviceName) => {
  if (!serviceName || !MICROSERVICE_CONFIG[serviceName]) {
    return url;
  }
  
  if (USE_LOCAL_MICROSERVICE_SETUP) {
    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    const parts = cleanUrl.split('/');
    if (parts[0] === serviceName) {
      parts.shift();
    }
    return '/' + parts.join('/');
  }
  
  const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
  if (!cleanUrl.startsWith(serviceName + '/') && !cleanUrl.startsWith(serviceName)) {
    return `${MICROSERVICE_CONFIG[serviceName].basePath}/${cleanUrl}`;
  }
  
  return cleanUrl;
};

/**
 * Prepare headers for API requests with CORS and authorization
 */
const prepareHeadersWithCors = (headers, { getState }, body) => {
  const token = getState().auth.token?.access_token;
  
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Don't set Content-Type header for FormData - let the browser set it with boundary
  // FormData needs multipart/form-data with proper boundary, not application/json
  if (!(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  
  return headers;
};

/**
 * Dynamic base query that selects the appropriate service URL
 */
const dynamicBaseQuery = async (args, api, extraOptions) => {
  const { url, body, ...rest } = args;
  
  const serviceName = extractServiceName(url);
  const baseUrl = getServiceBaseUrl(serviceName);
  const formattedPath = formatServicePath(url, serviceName);
  
  // Create a service-specific query function with custom prepareHeaders
  // that can access the body to check for FormData
  const serviceQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, apiArgs) => {
      // Pass the body to prepareHeadersWithCors so it can check if it's FormData
      return prepareHeadersWithCors(headers, apiArgs, body);
    },
    credentials: 'include', // Required for CORS with authentication
  });
  
  // Log the request details in local mode
  if (USE_LOCAL_MICROSERVICE_SETUP) {
    console.group('API Request');
    console.log('Service:', serviceName);
    console.log('Base URL:', baseUrl);
    console.log('Path:', formattedPath);
    console.log('Method:', rest.method || 'GET');
    console.groupEnd();
  }
  
  // Execute the query with the appropriate URL
  return serviceQuery(
    { 
      ...rest, 
      url: formattedPath,
      body: body
    }, 
    api, 
    extraOptions
  );
};

let refreshTokenPromise = null;

const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/verifyotp',
  '/auth/refresh',
  '/auth/resend-mfa-otp',
  'forget-password',
  'reset-password',
];

const isPublicAuthEndpoint = (url = '') => {
  const normalized = url.toLowerCase();
  return PUBLIC_AUTH_ENDPOINTS.some((path) => normalized.includes(path));
};

const refreshAccessToken = async (api) => {
  const refresh_token = api.getState().auth.token?.refresh_token;
  if (!refresh_token) return null;

  const url = getAxiosUrl('trial-mgmt/auth/refresh');
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.access_token) {
      api.dispatch(updateAccessTokens(data));
      return data;
    }
  } catch (e) {
    console.error('Token refresh failed', e);
  }
  return null;
};

const tryRefreshAccessToken = (api) => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = refreshAccessToken(api).finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

let sessionExpiryInProgress = false;

const LOGOUT_TIMEOUT_MS = 3000;

/** Best-effort server logout; does not block client session clear. */
const revokeServerSession = async (accessToken) => {
  if (!accessToken) return;

  try {
    const url = getAxiosUrl('trial-mgmt/auth/logout');
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), LOGOUT_TIMEOUT_MS)
      : null;
    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        ...(controller ? { signal: controller.signal } : {}),
      });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  } catch (e) {
    console.warn('Server logout failed during session expiry', e);
  }
};

const handleSessionExpired = async (api) => {
  const { application, auth } = api.getState();
  if (
    sessionExpiryInProgress ||
    window.location.pathname === '/login' ||
    application?.sessionExpiredDialog
  ) {
    return;
  }

  sessionExpiryInProgress = true;
  try {
    // Capture token before clear; revoke in background so UI is not blocked.
    const accessToken = auth?.token?.access_token;
    let savedReturnUrl = null;
    try {
      savedReturnUrl = localStorage.getItem(RETURN_URL_STORAGE_KEY);
    } catch (_) {}
    const currentPath = window.location.pathname + (window.location.search || '');
    if (!savedReturnUrl && isValidReturnPath(currentPath)) {
      savedReturnUrl = currentPath;
    }

    // Dialog flag first so LoginRouteGuard toast is not dropped; then clear auth.
    api.dispatch(openSessionExpiredDialog(true));
    api.dispatch({ type: 'bloqcibeApi/resetApiState' });
    api.dispatch(clearAuth());
    try {
      localStorage.clear();
      if (savedReturnUrl) {
        localStorage.setItem(RETURN_URL_STORAGE_KEY, savedReturnUrl);
      }
    } catch (_) {}

    // Fire-and-forget; do not await (avoids Wrong Navigation during hung logout).
    void revokeServerSession(accessToken);
  } finally {
    sessionExpiryInProgress = false;
  }
};

const handleUnauthorized = async (args, api, extraOptions, executeQuery) => {
  const { application } = api.getState();
  // Block stale authed calls after session expiry, but always allow public auth endpoints (login, OTP, etc.)
  if (application?.sessionExpiredDialog && !isPublicAuthEndpoint(args.url)) {
    return { error: { status: 401, data: { message: 'Session expired' } } };
  }

  let result = await executeQuery(args, api, extraOptions);

  if (result.error?.status !== 401 || isPublicAuthEndpoint(args.url)) {
    return result;
  }

  const refreshed = await tryRefreshAccessToken(api);
  if (refreshed) {
    result = await executeQuery(args, api, extraOptions);
  }

  if (result.error?.status === 401) {
    await handleSessionExpired(api);
  }

  return result;
};

/**
 * Enhanced base query with authentication and error handling
 */
const baseQueryWithReauthLocal = async (args, api, extraOptions) => {
  try {
    let result = await handleUnauthorized(args, api, extraOptions, dynamicBaseQuery);

    if (result.error) {
      if (result.error.status === 'FETCH_ERROR') {
        api.dispatch(openMessage({
          message: "Could not connect to the service. Please check your network connection.",
          messageSeverity: 'error'
        }));
        
        // Log more detailed error info in local mode
        if (USE_LOCAL_MICROSERVICE_SETUP) {
          console.error('Fetch error details:', result.error);
        }
      }
    }
    
    return result;
  } catch (error) {
    // Handle unexpected errors
    console.error("API request failed:", error);
    
    api.dispatch(openMessage({
      message: "An unexpected error occurred while processing your request.",
      messageSeverity: 'error'
    }));
    
    return { error: { status: 'FETCH_ERROR', data: error.message } };
  }
};

/**
 * Helper function to build a full URL for Axios requests
 */
const getAxiosUrl = (endpoint) => {
  // If using local microservice setup, use the dynamic routing
  if (USE_LOCAL_MICROSERVICE_SETUP) {
    if (!endpoint) return API_ENDPOINT;
    
    const serviceName = extractServiceName(endpoint);
    const baseUrl = getServiceBaseUrl(serviceName);
    const formattedPath = formatServicePath(endpoint, serviceName);
    
    const fixedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const fixedPath = formattedPath.startsWith('/') ? formattedPath : '/' + formattedPath;
    
    return `${fixedBaseUrl}${fixedPath}`;
  }
  
  // For deployed setup, use the API_ENDPOINT directly
  const fixedBaseUrl = API_ENDPOINT.endsWith('/') ? API_ENDPOINT.slice(0, -1) : API_ENDPOINT;
  const fixedPath = endpoint.startsWith('/') ? endpoint : '/' + endpoint;
  return `${fixedBaseUrl}${fixedPath}`;
};

// ============================================================================
// DEPLOYED SETUP - Base Query with single API endpoint
// ============================================================================
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_ENDPOINT_URL,
  // baseUrl: "https://api.dev.bloqcube.com/",
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = getState().auth.token?.access_token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      headers.set('Access-Control-Allow-Origin', '*');
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await handleUnauthorized(args, api, extraOptions, baseQuery);
  if (result.error && (result.error.status === 500 || result.error.status === 400)) {
    // api.dispatch(openMessage({
    //     message: "Something went wrong!",
    //     messageSeverity: 'error'
    // }))
  } else if (result.error && result.error.status == 'FETCH_ERROR') {
    // api.dispatch(openMessage({
    //     message: "Service not available!",
    //     messageSeverity: 'error'
    // }))
  }
  return result;
};
// ============================================================================

export const bloqcibeApi = createApi({
  reducerPath: 'bloqcibeApi',
  tagTypes: [
    'Trial',
    'TrialDocuments',
    'SiteList',
    'SiteBasicDetail',
    'TrialSiteDetail',
    'SubjectList',
    'SubjectDashboardDetails',
    'FormConfig',
    'EnrollSubjectFormConfig',
    'DepartmentMeta',
    'SubjectWithdrawList',
    'OnHoldSubjectList',
    'TrialBookingSlots',
    'userDetails',
    'UserBookedSlots',
    'DOATasks',
    'Sponsors',
    'ConfigurationList',
    'SponserConfigurationList',
    'allMessages',
    'UserContacts',
    'financeTrial',
    'financeTrials',
    'financeTrialSite',
    'siteSubjects',
    'entitites',
    'siteTransactions',
    'invoices',
    'LatestQueryTag',
    'userSignaturePreferences',
    'SponsorLevelUsers',
    
    // V2 API
    'TrialV2',
    'SiteListV2',
    'TrialSiteDetailV2',
    'userPreferences',
    
    // FAQ Module
    'FaqCategories',
    'Faqs',
    'PublicFaqs',
    // Support Tickets
    'SupportTickets',
    'SupportTicketDetail',
    'MySupportTickets',
    'MySupportTicketDetail',
  ],
  baseQuery: USE_LOCAL_MICROSERVICE_SETUP ? baseQueryWithReauthLocal : baseQueryWithReauth,
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: 'trial-mgmt/auth/login',
        // url: 'system-admin/auth/login',
        method: 'POST',
        body: credentials,
      }),
    }),
    verifyAuthOTP: builder.mutation({
      query: (body) => ({
        url: 'trial-mgmt/auth/verifyOTP',
        method: 'POST',
        body: body,
      }),
    }),
    resendMfaOtp: builder.mutation({
      query: (credentials) => ({
        url: 'trial-mgmt/auth/resend-mfa-otp',
        method: 'POST',
        body: credentials,
      }),
    }),
    refreshToken: builder.mutation({
      query: (body) => ({
        url: 'trial-mgmt/auth/refresh',
        method: 'POST',
        body,
      }),
    }),
    logout: builder.mutation({
      query: () => ({
        url: 'trial-mgmt/auth/logout',
        method: 'POST',
      }),
    }),
    forgetPassword: builder.mutation({
      query: ({ email }) => ({
        url: `/trial-mgmt/password/forget-password/${email}`,
        method: 'PUT',
      }),
    }),
    resetPassword: builder.mutation({
      query: ({ payload }) => ({
        url: `/trial-mgmt/sponsor/reset-password`,
        method: 'PUT',
        body: payload,
      }),
    }),

    setProfile: builder.mutation({
      query: ({ sponsorId, userId, payload }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/user/${userId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['userDetails'],
    }),
    verifyOTP: builder.mutation({
      query: ({ payload }) => ({
        url: `/trial-mgmt/sponsor/verifyOTP`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['userDetails'],
    }),
    resendOTP: builder.mutation({
      query: ({ payload }) => ({
        url: `/trial-mgmt/sponsor/resendOTP`,
        method: 'POST',
        body: payload,
      }),
    }),

    createTrial: builder.mutation({
      query: ({ trialPayload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial`,
        method: 'POST',
        body: trialPayload,
      }),
      invalidatesTags: ['Trial'],
    }),

    // V2 API
    createTrialV2: builder.mutation({
      query: ({ trialPayload, sponsorId }) => ({
        url: `/trial-mgmt/v2/sponsor/${sponsorId}/trial`,
        method: 'POST',
        body: trialPayload,
      }),
      invalidatesTags: ['TrialV2'],
    }),

    updateTrialBasicDetails: builder.mutation({
      query: ({ trialPayload, trialId, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}`,
        method: 'PATCH',
        body: trialPayload,
      }),
      invalidatesTags: ['Trial'],
    }),
    getTrialDocuments: builder.query({
      query: ({ trialId, sponsorId, data }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/document/list`,
        method: 'POST',
        body: data,
      }),
      providesTags: ['TrialDocuments'],
    }),
    getSiteDocuments: builder.query({
      query: ({ trialId, sponsorId, siteId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/document/list`,
        method: 'POST',
        body: { siteId: siteId },
      }),
    }),
    uploadDoc: builder.mutation({
      queryFn: async ({ url, formData, trialId, sponsorId }, api) => {
        try {
          const token = api.getState().auth.token?.access_token;
          const result = await axios.post(
            getAxiosUrl(`/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/document`),
            // `${process.env.REACT_APP_API_ENDPOINT_URL}trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/document`,
            formData,
            {
              headers: {
                // Don't set Content-Type header - axios will automatically set it with proper boundary for FormData
                Authorization: `Bearer ${token}`,
              },
              onUploadProgress: (upload) => {
                //Set the progress value to show the progress bar
                let uploadloadProgress = Math.round((100 * upload.loaded) / upload.total);
                //console.log("uploadloadProgress", uploadloadProgress);
                api.dispatch(setProgress(uploadloadProgress));
                //api.dispatch(setUploadProgress(uploadloadProgress));
              },
            }
          );
          api.dispatch(setProgress(100));
          return { data: result.data };
        } catch (axiosError) {
          let err = axiosError;
          return {
            error: {
              status: err.response?.status,
              data: err.response?.data || err.message,
            },
          };
        }
      },
      invalidatesTags: ['TrialDocuments'],
    }),
    createTrialPatch: builder.mutation({
      query: ({ id, trialPayload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${id}/basic-detail`,
        method: 'PATCH',
        body: trialPayload,
      }),
      invalidatesTags: ['Trial'],
    }),
    createTrialPatchV2: builder.mutation({
      query: ({ id, trialPayload, sponsorId }) => ({
        url: `/trial-mgmt/v2/sponsor/${sponsorId}/trial/${id}/basic-detail`,
        method: 'PATCH',
        body: trialPayload,
      }),
      invalidatesTags: ['TrialV2'],
    }),
    addSiteMember: builder.mutation({
      query: ({ sponsorId, data }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/user`,
        method: 'POST',
        body: data,
      }),
    }),
    assignRole: builder.mutation({
      query: ({ sponsorId, data }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/assign-role`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TrialSiteDetail', 'UserContacts', 'TrialSiteDetailV2'],
    }),
    createTrialSitePatch: builder.mutation({
      query: ({ id, siteDetailsPayload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${id}/site`,
        method: 'POST',
        body: siteDetailsPayload,
      }),
      invalidatesTags: ['SiteList'],
    }),
    createTrialSitePatchV2: builder.mutation({
      query: ({ id, siteDetailsPayload, sponsorId }) => ({
        url: `/trial-mgmt/v2/sponsor/${sponsorId}/trial/${id}/site`,
        method: 'POST',
        body: siteDetailsPayload,
      }),
      invalidatesTags: ['SiteListV2'],
    }),
    getTrials: builder.mutation({
      query: ({ filter, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/user-trials`,
        method: 'POST',
        body: {
          filter: filter,
        },
      }),
    }),
    getTrialDetails: builder.query({
      query: ({ id, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${id}`,
      }),
      providesTags: (result, error, arg) => {
        return result ? [{ type: 'Trial', id: result.id }, 'Trial'] : ['Trial'];
      },
    }),
    getTrialDetailsV2: builder.query({
      query: ({ id, sponsorId }) => ({
        url: `/trial-mgmt/v2/sponsor/${sponsorId}/trial/${id}`,
      }),
      providesTags: (result, error, arg) => {
        return result ? [{ type: 'TrialV2', id: result.id }, 'TrialV2'] : ['TrialV2'];
      },
    }),
    getOngoingTrials: builder.query({
      query: (sponsorId) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trials/on-going`,
      }),
      providesTags: (result, error, arg) => {
        return result ? [{ type: 'Trial', id: result.id }, 'Trial'] : ['Trial'];
      },
    }),
    getTrialSiteDetails: builder.query({
      query: ({ trialId, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/sites`,
      }),
      providesTags: ['SiteList'],
    }),

    getTrialSiteDetailsV2: builder.query({
      query: ({ trialId, sponsorId }) => ({
        url: `/trial-mgmt/v2/sponsor/${sponsorId}/trial/${trialId}/sites`,
      }),
      providesTags: ['SiteListV2'],
    }),
    getTrialVisitDetails: builder.query({
      query: ({ trialId }) => ({
        url: `/e-consent/crf/step-master/trial/${trialId}`,
      }),
    }),
    getSiteBasicDetails: builder.query({
      query: ({ sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/sites`,
      }),
      providesTags: ['SiteBasicDetail'],
    }),
    getSiteMembers: builder.query({
      query: ({ sponsorId, siteId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/site/${siteId}/users`,
      }),
    }),
    getTrialSiteMembers: builder.query({
      query: ({ sponsorId, trialId, siteId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/assign-users`,
      }),
    }),
    deleteTrialDocuments: builder.mutation({
      query: ({ trialId, s3Key, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/delete-document`,
        method: 'DELETE',
        body: { key: s3Key },
      }),
      invalidatesTags: ['TrialDocuments'],
    }),
    deleteUserRole: builder.mutation({
      query: ({ user_role_id }) => ({
        url: `/trial-mgmt/sponsor/user/${user_role_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TrialSiteDetail', 'UserContacts', 'TrialSiteDetailV2'],
    }),
    getDownloadTrialDoc: builder.query({
      queryFn: async ({ trialId, fileName, s3Key, sponsorId }, api) => {
        try {
          const token = api.getState().auth.token?.access_token;
          const response = await axios.post(
            getAxiosUrl(`/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/download-document`),
            // `${process.env.REACT_APP_API_ENDPOINT_URL}trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/download-document`,
            { key: s3Key },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          var byteArray = base64ToArrayBuffer(response.data);
          var a = window.document.createElement('a');
          a.href = window.URL.createObjectURL(new Blob([byteArray], { type: 'application/octet-stream' }));
          a.download = decodeURIComponent(fileName);
          document.body.appendChild(a);
          a.click();
        } catch (axiosError) {
          let err = axiosError;
          return {
            error: {
              status: err.response?.status,
              data: err.response?.data || err.message,
            },
          };
        }
      },
    }),
    getTrialDocTypes: builder.query({
      query: () => ({
        url: `/trial-mgmt/doctypes`,
      }),
    }),
    updateTrialBankDetail: builder.mutation({
      query: ({ sponsorId, trialId, bankDetails }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/bank-detail/${bankDetails.id}`,
        method: 'PATCH',
        body: bankDetails,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Trial', id: arg.trialId }],
    }),
    updateTrialStudyDetail: builder.mutation({
      query: ({ sponsorId, trialId, studyDetail }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/study/${studyDetail.studyDetail.id}`,
        method: 'PATCH',
        body: studyDetail,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Trial', id: arg.trialId }],
    }),
    updateTrialPatch: builder.mutation({
      query: ({ id, trialPayload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${id}`,
        method: 'PATCH',
        body: trialPayload,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Trial', id: arg.trialId }],
    }),
    ///trial-mgmt/sponsor/{sponsorId}/trial/{trialId}/trial-budget
    updateTrialBudgetDetail: builder.mutation({
      query: ({ sponsorId, trialId, budgetDetail }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/trial-budget`,
        method: 'PATCH',
        body: budgetDetail,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Trial', id: arg.trialId }],
    }),
    getSiteDocumentMeta: builder.query({
      query: () => ({
        url: '/trial-mgmt/site/doctypes',
      }),
    }),
    markTrialSetupDone: builder.mutation({
      query: ({ sponsorId, trialId, status }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/${status}`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Trial', id: arg.trialId }, 'Trial'],
    }),
    markTrialSetupDoneV2: builder.mutation({
      query: ({ sponsorId, trialId, status }) => ({
        url: `/trial-mgmt/v2/sponsor/${sponsorId}/trial/${trialId}/${status}`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'TrialV2', id: arg.trialId }, 'TrialV2'],
    }),
    getDepartmentMeta: builder.query({
      query: () => ({
        url: '/trial-mgmt/department',
      }),
      providesTags: ['DepartmentMeta'],
    }),
    getDynamicDataValues: builder.query({
      query: ({ url }) => ({
        url: url,
      }),
    }),
    createSite: builder.mutation({
      query: ({ sponsorId, siteData }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/site`,
        body: siteData,
        method: 'POST',
      }),
      invalidatesTags: ['SiteBasicDetail'],
    }),
    getTrialSiteInfo: builder.query({
      query: ({ sponsorId, trialId, siteTrialId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/site-trial/${siteTrialId}`,
      }),
      providesTags: ['TrialSiteDetail'],
    }),
    getTrialSiteInfoV2: builder.query({
      query: ({ sponsorId, trialId, siteTrialId }) => ({
        url: `/trial-mgmt/v2/sponsor/${sponsorId}/trial/${trialId}/site-trial/${siteTrialId}`,
      }),
      providesTags: ['TrialSiteDetailV2'],
    }),
    getLibraryForm: builder.query({
      // For this query, we are passing the complete URL as 'path', so we skip any MICROSERVICE_CONFIG path update.
      queryFn: async (path, api) => {
        try {
          const token = api.getState().auth.token?.access_token;
          const response = await axios.get(path, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
          return { data: response.data };
        } catch (axiosError) {
          return {
            error: {
              status: axiosError?.response?.status,
              data: axiosError?.response?.data || axiosError?.message,
            },
          };
        }
      },
      providesTags: ['FormConfig'],
    }),

    getJsonForm: builder.query({
      queryFn: async ({ sponsorId, documentKey }, api) => {
        try {
          const token = api.getState().auth.token?.access_token;
          const response = await axios.get(
            getAxiosUrl(`/form-library/library/file/${sponsorId}/${documentKey}`),
            // `${process.env.REACT_APP_API_ENDPOINT_URL}form-library/library/file/${sponsorId}/${documentKey}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response?.data) {
            const decodedData = atob(response.data);
            const jsonData = JSON.parse(decodedData);

            const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `${documentKey}.json`;

            document.body.appendChild(a);
            a.click();
            a.remove();
            return '';
          } else {
            return '';
          }
        } catch (axiosError) {
          return {
            error: {
              status: axiosError?.response?.status,
              data: axiosError?.response?.data || axiosError?.message,
            },
          };
        }
      },
    }),
    uploadSignature: builder.mutation({
      query: ({ sponsorId, trialId, trialSiteId, payload }) => ({
        url: `/trial-mgmt/questionnaire/sponsor/${sponsorId}/trial/${trialId}/trial-site/${trialSiteId}/signture-upload`,
        body: payload,
        method: 'POST',
      }),
    }),
    uploadVisitSignature: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/crf/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/signture-upload`,
        body: payload,
        method: 'POST',
      }),
    }),
    uploadCRFFile: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/crf/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/document-upload`,
        body: payload,
        method: 'POST',
      }),
    }),
    uploadInitiationFile: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/trial-mgmt/questionnaire/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/document-upload`,
        body: payload,
        method: 'POST',
      }),
    }),
    uploadSubjectProfilePic: builder.mutation({
      query: ({ subjectMasterId, payload }) => ({
        url: `/e-consent/subject/${subjectMasterId}/profile-pic`,
        body: payload,
        method: 'POST',
      }),
      invalidatesTags: ['SubjectDashboardDetails'],
    }),
    uploadSubjectFile: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/document-upload`,
        body: payload,
        method: 'POST',
      }),
    }),
    downloadSignature: builder.query({
      query: ({ s3Key }) => ({
        url: `/trial-mgmt/questionnaire/signture`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    downloadSubjectProfilePic: builder.query({
      query: ({ s3Key }) => ({
        url: `/e-consent/subject/subject/profile-pic`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    downloadVisitSignature: builder.query({
      query: ({ s3Key }) => ({
        url: `/e-consent/crf/signture`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    downloadCRFFile: builder.query({
      query: ({ s3Key }) => ({
        url: `/e-consent/crf/document`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    downloadInitiationFile: builder.query({
      query: ({ s3Key }) => ({
        url: `/trial-mgmt/questionnaire/document`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    downloadSubjectFile: builder.query({
      query: ({ s3Key }) => ({
        url: `/e-consent/subject/document`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    downloadSubjectSignature: builder.query({
      query: ({ s3Key }) => ({
        url: `/e-consent/subject/signture`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    submitSiteInitAnswers: builder.mutation({
      query: ({ sponsorId, trialId, trialSiteId, payload }) => ({
        url: `/trial-mgmt/questionnaire/sponsor/${sponsorId}/trial/${trialId}/trial-site/${trialSiteId}`,
        body: payload,
        method: 'POST',
      }),
    }),
    updateSiteInitAnswers: builder.mutation({
      query: ({ sponsorId, trialId, trialSiteId, payload }) => ({
        url: `/trial-mgmt/questionnaire/sponsor/${sponsorId}/trial/${trialId}/trial-site/${trialSiteId}`,
        body: payload,
        method: 'PUT',
      }),
    }),
    verifySiteInitAnswers: builder.mutation({
      query: ({ sponsorId, trialId, trialSiteId, payload }) => ({
        url: `/trial-mgmt/questionnaire/sponsor/${sponsorId}/trial/${trialId}/trial-site/${trialSiteId}`,
        body: payload,
        method: 'PATCH',
      }),
    }),
    getTrialSiteAnswers: builder.query({
      query: ({ sponsorId, trialId, trialSiteId, payload }) => ({
        url: `/trial-mgmt/questionnaire/sponsor/${sponsorId}/trial/${trialId}/trial-site/${trialSiteId}/site-init-answers`,
        body: payload,
        method: 'POST',
      }),
    }),
    getVisitAnswers: builder.query({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/crf/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/subject-answers`,
        body: payload,
        method: 'POST',
      }),
    }),
    getSponsorDetails: builder.query({
      query: (sponsorId) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}`,
      }),
    }),
    getSponsorLogo: builder.query({
      query: ({ key, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/logo/${key}`,
      }),
    }),
    saveSubjectEnrollInfo: builder.mutation({
      query: ({ trialId, siteId, sponsorId, payload }) => ({
        method: 'POST',
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}`,
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => {
        if (result?.id) {
          return [{ type: 'SubjectList', id: result.id }, 'SubjectList'];
        } else {
          return ['SubjectList'];
        }
      },
    }),
    saveVisitAnswers: builder.mutation({
      query: ({ trialId, siteId, sponsorId, payload }) => ({
        method: 'POST',
        url: `/e-consent/crf/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}`,
        body: payload,
      }),
      // invalidatesTags: (result, error, arg) => error ? [] : [{ type: 'SubjectList', id: result.id }, 'SubjectList'],
    }),
    saveWithdrawSubAnswers: builder.mutation({
      query: ({ trialId, siteId, sponsorId, payload }) => ({
        method: 'POST',
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/withdrawl`,
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'SubjectList', id: result.id },
        'SubjectList',
        'SubjectWithdrawList',
      ],
    }),
    saveComment: builder.mutation({
      query: ({ payload }) => ({
        method: 'POST',
        url: `/e-consent/crf/comment`,
        body: payload,
      }),
    }),
    createQuery: builder.mutation({
      query: (payload) => ({
        method: 'POST',
        url: `/e-consent/query-management`,
        body: payload,
      }),
      invalidatesTags: ['LatestQueryTag'],
    }),
    uploadQueryAttachments: builder.mutation({
      query: ({ queryId, formData }) => ({
        method: 'POST',
        url: `/e-consent/query-management/${queryId}/attachments`,
        body: formData,
        // Don't set Content-Type header, let the browser set it with boundary for multipart/form-data
      }),
    }),
    getQueries: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
          if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            queryParams.append(key, params[key]);
          }
        });
        return {
          url: `/e-consent/query-management?${queryParams.toString()}`,
        };
      },
    }),
    getQuery: builder.query({
      query: (queryId) => ({
        url: `/e-consent/query-management/${queryId}`,
      }),
    }),
    getLatestQueryTag: builder.query({
      query: ({ sponsorId, trialId, siteId }) => ({
        url: `/e-consent/query-management/latest-tag/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}`,
      }),
      providesTags: ['LatestQueryTag'],
    }),
    exportQueries: builder.mutation({
      query: (payload) => ({
        method: 'POST',
        url: `/e-consent/query-management/export`,
        body: payload,
        responseHandler: (response) => response.blob(),
      }),
    }),
    updateQuery: builder.mutation({
      query: ({ id, data }) => ({
        method: 'PATCH',
        url: `/e-consent/query-management/${id}`,
        body: data,
      }),
      invalidatesTags: ['LatestQueryTag'],
    }),
    addQueryComment: builder.mutation({
      query: ({ queryId, data }) => ({
        method: 'POST',
        url: `/e-consent/query-management/${queryId}/comments`,
        body: data,
      }),
    }),
    uploadQueryCommentAttachments: builder.mutation({
      query: ({ queryId, formData }) => ({
        method: 'POST',
        url: `/e-consent/query-management/${queryId}/comments-with-attachments`,
        body: formData,
      }),
    }),
    addQueryAssignee: builder.mutation({
      query: ({ queryId, data }) => ({
        method: 'POST',
        url: `/e-consent/query-management/${queryId}/assignees`,
        body: data,
      }),
    }),
    deleteQueryAssignee: builder.mutation({
      query: ({ queryId, userId }) => ({
        method: 'DELETE',
        url: `/e-consent/query-management/${queryId}/assignees/${userId}`,
      }),
    }),
    downloadQueryAttachment: builder.query({
      queryFn: async ({ attachmentId }, api) => {
        try {
          const token = api.getState().auth.token?.access_token;
          const response = await axios.get(
            getAxiosUrl(`/e-consent/query-management/attachments/${attachmentId}/download`),
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob',
            }
          );

          // Get filename from Content-Disposition header or use default
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'attachment';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
            if (fileNameMatch) {
              fileName = decodeURIComponent(fileNameMatch[1]);
            }
          }

          // Create blob URL and trigger download
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/octet-stream',
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          return { data: { success: true } };
        } catch (axiosError) {
          let err = axiosError;
          return {
            error: {
              status: err.response?.status,
              data: err.response?.data || err.message,
            },
          };
        }
      },
    }),
    getSubjectList: builder.query({
      query: ({ sponsorId, trialId, siteId, page, pageSize, formStatusFilters, search }) => {
        // Build query params
        const params = new URLSearchParams();
        if (page !== undefined) params.append('page', page.toString());
        if (pageSize !== undefined) params.append('pageSize', pageSize.toString());
        if (formStatusFilters) params.append('formStatusFilters', formStatusFilters);
        if (search) params.append('search', search);
        
        const queryString = params.toString();
        return {
          url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/subjects${queryString ? `?${queryString}` : ''}`,
        };
      },
      providesTags: (result, error, arg) => {
        // Create unique tag based on pagination and filter params
        return [
          { type: 'SubjectList', id: `${arg.sponsorId}-${arg.trialId}-${arg.siteId}-${arg.page}-${arg.pageSize}-${arg.formStatusFilters}-${arg.search}` },
          'SubjectList'
        ];
      },
    }),
    getWithdrawSubjectList: builder.query({
      query: ({ sponsorId, trialId, siteId }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/withdraw-subjects`,
      }),
      providesTags: ['SubjectWithdrawList'],
    }),
    getOnHoldSubjectList: builder.query({
      query: ({ sponsorId, trialId, siteId }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/subjects/status/On_Hold`,
      }),
      providesTags: ['OnHoldSubjectList'],
    }),
    reEnrollSubject: builder.mutation({
      query: ({ sponsorId, trialId, siteId, subjectMasterId, reason, locationLatitude, locationLongitude }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/re-enroll`,
        method: 'POST',
        body: {
          subjectMasterId,
          reason,
          locationLatitude,
          locationLongitude,
        },
      }),
      invalidatesTags: ['OnHoldSubjectList', 'SubjectList', 'SubjectDetail'],
    }),
    getSubjectAnswers: builder.query({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/subject-answers`,
        method: 'POST',
        body: payload,
      }),
    }),
    getComments: builder.query({
      query: ({ sponsorId, trialId, trialSiteId, subjectMasterId, payload }) => ({
        url: `/e-consent/crf/comment/sponsor/${sponsorId}/trial/${trialId}/trial-site/${trialSiteId}/subject/${subjectMasterId}`,
        method: 'POST',
        body: payload,
      }),
    }),
    getFieldHistory: builder.query({
      query: ({ payload }) => ({
        url: `/e-consent/crf/fieldHistory`,
        method: 'POST',
        body: payload,
      }),
    }),
    getSubjectFieldHistory: builder.query({
      query: ({ payload }) => ({
        url: `/e-consent/subject/fieldHistory`,
        method: 'POST',
        body: payload,
      }),
    }),
    uploadSubjectSignature: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/signture-upload`,
        body: payload,
        method: 'POST',
      }),
    }),
    getSubjectDetail: builder.query({
      query: (subjectMasterId) => ({
        url: `/e-consent/subject/${subjectMasterId}`,
      }),
      providesTags: (result, error, arg) => {
        return result ? [{ type: 'SubjectList', id: result.id }, 'SubjectList'] : ['SubjectList'];
      },
    }),
    getSubjectDashboardDetail: builder.query({
      query: (subjectMasterId) => ({
        url: `/e-consent/subject/subject/${subjectMasterId}/detail`,
      }),
      providesTags: ['SubjectDashboardDetails'],
    }),
    addFieldSubjectEnrollment: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/subject/${sponsorId}/trial/${trialId}/site/${siteId}/custom-field`,
        body: payload,
        method: 'POST',
      }),
      //invalidatesTags:["FormConfig"]
    }),
    addFieldSiteInitiation: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/trial-mgmt/questionnaire/sponsor/${sponsorId}/trial/${trialId}/custom-field`,
        body: payload,
        method: 'POST',
      }),
      //invalidatesTags:["FormConfig"]
    }),
    removeFieldSubjectEnrollment: builder.mutation({
      query: (fieldId) => ({
        url: `/e-consent/subject/custom-field/${fieldId}`,
        method: 'DELETE',
      }),
    }),
    removeFieldSiteInitiation: builder.mutation({
      query: (fieldId) => ({
        url: `/trial-mgmt/questionnaire/custom-field/${fieldId}`,
        method: 'DELETE',
      }),
    }),
    generateOTPValue: builder.mutation({
      query: () => ({
        url: `/trial-mgmt/sponsor/generateOTP`,
        body: {},
        method: 'POST',
      }),
    }),
    playConsentAudio: builder.mutation({
      queryFn: async ({ payload }, api) => {
        try {
          const token = api.getState().auth.token?.access_token;
          const response = await axios.post(
            getAxiosUrl(`/e-consent/subject/consent/synthesizeSpeech`),
            // `${process.env.REACT_APP_API_ENDPOINT_URL}e-consent/subject/consent/synthesizeSpeech`,
            payload,
            { headers: { Authorization: `Bearer ${token}` }, responseType: 'stream' }
          );
          return { data: response.data };
        } catch (axiosError) {
          let err = axiosError;
          return {
            error: {
              status: err.response?.status,
              data: err.response?.data || err.message,
            },
          };
        }
      },
    }),
    resendOTPValue: builder.mutation({
      query: () => ({
        url: `/trial-mgmt/sponsor/resendOTP`,
        body: {},
        method: 'POST',
      }),
    }),
    verifyOTPValue: builder.mutation({
      query: ({ payload }) => ({
        url: `/trial-mgmt/sponsor/verifyOTP`,
        body: payload,
        method: 'POST',
      }),
    }),
    getEConsentFormConfig: builder.query({
      query: ({ sponsorId, trialId, siteId, documentKey, payload }) => ({
        url: `/e-consent/subject/${sponsorId}/trial/${trialId}/site/${siteId}/subject-enroll/${documentKey}`,
        body: payload,
        method: 'POST',
      }),
      providesTags: ['EnrollSubjectFormConfig'],
    }),
    getSiteInitiationConfig: builder.query({
      query: ({ sponsorId, trialId, documentKey, siteId }) => ({
        url: `/trial-mgmt/questionnaire/sponsor/${sponsorId}/trial/${trialId}/site-initiation/${documentKey}`,
        body: { siteId: siteId },
        method: 'POST',
      }),
      providesTags: ['EnrollSubjectFormConfig'],
    }),
    externalVerifySubject: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/verify`,
        body: payload,
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => ['SubjectList'],
    }),
    externalVerifyCRF: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/crf/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/verify`,
        body: payload,
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'SubjectList', id: result.id }, 'SubjectList'],
    }),
    AddDepartmentInMeta: builder.mutation({
      query: (dept) => ({
        url: '/trial-mgmt/department',
        method: 'POST',
        body: { title: dept },
      }),
      invalidatesTags: ['DepartmentMeta'],
    }),
    getSubjectListByField: builder.mutation({
      query: ({ sponsorId, trialId, siteId, payload }) => ({
        url: `/e-consent/crf/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/subjects`,
        body: payload,
        method: 'POST',
      }),
    }),
    createSchedule: builder.mutation({
      query: ({ trialId, siteId, payload }) => ({
        url: `/trial-mgmt/schedule/trial/${trialId}/site/${siteId}`,
        body: payload,
        method: 'POST',
      }),
    }),
    getSchedule: builder.mutation({
      query: ({ trialId, payload }) => ({
        url: `/trial-mgmt/schedule/trial/${trialId}/events`,
        body: payload,
        method: 'POST',
      }),
    }),
    addCommentOnScheduleEvent: builder.mutation({
      query: ({ scheduleId, payload }) => ({
        url: `/trial-mgmt/schedule/schedule/${scheduleId}/comment`,
        body: payload,
        method: 'POST',
      }),
    }),
    getOneSchedule: builder.query({
      query: (scheduleId) => ({
        url: `/trial-mgmt/schedule/${scheduleId}`,
        method: 'GET',
      }),
    }),
    getMeUser: builder.query({
      query: () => ({
        url: `/trial-mgmt/sponsor/users/me`,
        method: 'GET',
      }),
      providesTags: ['userDetails'],
      // Keep data fresh but don't block on refetch
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: true,
    }),
    uploadUserProfilePic: builder.mutation({
      query: ({ payload }) => ({
        url: `/trial-mgmt/sponsor/user/profile-pic`,
        body: payload,
        method: 'POST',
      }),
      invalidatesTags: ['userDetails'],
    }),
    downloadUserProfilePic: builder.query({
      query: ({ s3Key }) => ({
        url: `/trial-mgmt/sponsor/user/profile-pic`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    // Signature Preferences APIs
    getSignaturePreferences: builder.query({
      query: () => ({
        url: `/trial-mgmt/sponsor/user/signature-preferences`,
        method: 'GET',
      }),
      providesTags: ['userSignaturePreferences'],
    }),
    updateSignaturePreferences: builder.mutation({
      query: ({ signaturePreference, signatureFont }) => ({
        url: `/trial-mgmt/sponsor/user/signature-preferences`,
        method: 'PUT',
        body: { signaturePreference, signatureFont },
      }),
      invalidatesTags: ['userSignaturePreferences'],
    }),
    saveUserSignature: builder.mutation({
      query: ({ payload }) => ({
        url: `/trial-mgmt/sponsor/user/saved-signature`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['userSignaturePreferences'],
    }),
    getUserSavedSignature: builder.query({
      query: () => ({
        url: `/trial-mgmt/sponsor/user/saved-signature`,
        method: 'GET',
      }),
      providesTags: ['userSignaturePreferences'],
    }),
    deleteUserSignature: builder.mutation({
      query: () => ({
        url: `/trial-mgmt/sponsor/user/saved-signature`,
        method: 'DELETE',
      }),
      invalidatesTags: ['userSignaturePreferences'],
    }),
    updateSchedule: builder.mutation({
      query: ({ scheduleId, trialId, siteId, payload }) => ({
        url: `/trial-mgmt/schedule/trial/${trialId}/site/${siteId}/schedule/${scheduleId}`,
        method: 'PUT',
        body: payload,
      }),
    }),
    createTrialBookingSlot: builder.mutation({
      query: ({ visitId, trialId, payload }) => ({
        url: `/trial-mgmt/schedule/booking/trial/${trialId}/visit/${visitId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['TrialBookingSlots'],
    }),
    getTrialBookingSlots: builder.query({
      query: () => ({
        url: `/trial-mgmt/schedule/booking/slots`,
      }),
      providesTags: ['TrialBookingSlots'],
    }),
    deleteTrialBooking: builder.mutation({
      query: ({ visitId, trialId, siteId, slotId }) => ({
        url: `/trial-mgmt/schedule/booking/slotId/${slotId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TrialBookingSlots'],
    }),
    updateTrialBookingSlot: builder.mutation({
      query: ({ slotId, payload }) => ({
        url: `/trial-mgmt/schedule/booking/slotId/${slotId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['TrialBookingSlots'],
    }),
    publishBookingSlot: builder.mutation({
      query: ({ visitId, trialId, payload }) => ({
        url: `/trial-mgmt/schedule/booking/trial/${trialId}/visit/${visitId}/publish`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['TrialBookingSlots'],
    }),
    dashboradData: builder.query({
      query: ({ data }) => ({
        url: `/trial-mgmt/v1/dashboard`,
        method: 'POST',
        body: data,
      }),
    }),
    financeDashboradData: builder.query({
      query: ({ data }) => ({
        url: `/finance-service/v1/dashboard`,
        method: 'POST',
        body: data,
      }),
    }),
    updateDashboardPreference: builder.mutation({
      query: (payload) => ({
        url: `/trial-mgmt/v1/dashboard/preferences`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    bookVisitSlot: builder.mutation({
      query: ({ slotId, userId }) => ({
        url: `/trial-mgmt/schedule/booking/slot/${slotId}/user/${userId}`,
        method: 'POST',
      }),
      invalidatesTags: ['UserBookedSlots'],
    }),
    cancelVisitSlot: builder.mutation({
      query: ({ slotId, userId }) => ({
        url: `/trial-mgmt/schedule/booking/slot/${slotId}/user/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserBookedSlots'],
    }),
    cancelScheduledEvent: builder.mutation({
      query: ({ scheduleId }) => ({
        url: `/trial-mgmt/schedule/${scheduleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserBookedSlots'],
    }),
    //DOA
    fetchDAO: builder.query({
      query: ({ sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trials/doa`,
      }),
    }),
    fetchAllAssigngedTasks: builder.query({
      query: ({ trialId, siteId }) => ({
        url: `/trial-mgmt/doa/assigned-task/trial/${trialId}/site/${siteId}`,
      }),
      providesTags: ['DOAAllAssignedTasks'],
    }),
    fetchUserAssigngedTasks: builder.query({
      query: ({ trialId, siteId, userId }) => ({
        url: `/trial-mgmt/doa/assigned-task/trial/${trialId}/site/${siteId}?userId=${userId}`,
      }),
      providesTags: ['DOAAllAssignedTasks'],
    }),
    fetchAllDAOTasks: builder.query({
      query: ({ trialId, siteId }) => ({
        url: `/trial-mgmt/doa/trial/${trialId}/site/${siteId}`,
      }),
      providesTags: ['DOATasks'],
    }),
    assignTask: builder.mutation({
      query: ({ userId, payload }) => ({
        url: `/trial-mgmt/doa/assign-tasks/user/${userId}`,
        method: 'POST',
        body: payload,
      }),
    }),
    deleteAssignedTask: builder.mutation({
      query: ({ assignedTaskId }) => ({
        url: `/trial-mgmt/doa/assigned-task/${assignedTaskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DOAAllAssignedTasks'],
    }),
    saveDOASign: builder.mutation({
      query: ({ assignedTaskId, payload }) => ({
        url: `/trial-mgmt/doa/assigned-task/${assignedTaskId}/document-upload`,
        method: 'POST',
        body: payload,
      }),
    }),
    changeDOATaskStatus: builder.mutation({
      query: ({ assignedTaskId, status }) => ({
        url: `/trial-mgmt/doa/assigned-task/${assignedTaskId}/status/${status}`,
        method: 'PATCH',
      }),
    }),
    bulkVerifyDOATasks: builder.mutation({
      query: (payload) => ({
        url: '/trial-mgmt/doa/bulk-task-verification',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['DOAAllAssignedTasks'],
    }),
    addDOAComment: builder.mutation({
      query: ({ assignedTaskId, payload }) => ({
        url: `/trial-mgmt/doa/assigned-task/${assignedTaskId}/comment`,
        method: 'POST',
        body: payload,
      }),
    }),
    downloadDOASignature: builder.query({
      query: ({ s3Key }) => ({
        url: `/trial-mgmt/doa/assigned-task/download-document`,
        method: 'GET',
        params: {
          key: s3Key,
        },
      }),
    }),
    deleteDOADocuments: builder.mutation({
      query: (s3Key) => ({
        url: `/trial-mgmt/doa/assigned-task/delete-document`,
        method: 'DELETE',
        params: {
          key: s3Key,
        },
      }),
    }),
    updateTask: builder.mutation({
      query: ({ payload, trialId, siteId, taskId }) => ({
        url: `/trial-mgmt/doa/trial/${trialId}/site/${siteId}/task/${taskId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['DOATasks'],
    }),
    updateAssignedTask: builder.mutation({
      query: ({ assignedTaskId, payload }) => ({
        url: `/trial-mgmt/doa/assigned-task/${assignedTaskId}`,
        method: 'PUT',
        body: payload,
      }),
    }),
    createDoaTask: builder.mutation({
      query: ({ payload }) => ({
        url: `/trial-mgmt/doa/task`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['DOATasks'],
    }),
    deleteDoaTask: builder.mutation({
      query: (taskId) => ({
        url: `/trial-mgmt/doa/task/${taskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DOATasks'],
    }),
    uploadDoaTaskDocument: builder.mutation({
      query: ({ assignedTaskId, payload }) => ({
        url: `/trial-mgmt/doa/assigned-task/${assignedTaskId}/document-upload`,
        body: payload,
        method: 'POST',
      }),
    }),
    getUserBookedSlots: builder.query({
      query: (userId) => ({
        url: `/trial-mgmt/schedule/booking/user/${userId}`,
      }),
      providesTags: ['UserBookedSlots'],
    }),
    getBookedTrialsSlots: builder.query({
      query: (payload) => ({
        url: `/trial-mgmt/schedule/booking/booked-slots`,
        method: 'POST',
        body: payload,
      }),
      providesTags: ['UserBookedSlots'],
    }),
    downloadAuditFile: builder.mutation({
      query: ({ query, payload }) => ({
        url: `/audit-log/export-csv${query}`,
        method: 'POST',
        body: payload,
        responseHandler: async (response) => {
          if (response.headers.get('Content-Type')?.includes('text/csv')) {
            // Handle CSV response
            return response.text();
          } else if (response.headers.get('Content-Type')?.includes('application/pdf')) {
            return response.blob();
          } else {
            // Handle JSON response
            return response.json();
          }
        },
      }),
    }),
    downloadFinanceAuditFile: builder.mutation({
      query: ({ query, protocolNumber }) => ({
        url: `/finance-service/audit-logs/export-csv/${protocolNumber}`,
        method: 'POST',
        query,
        responseHandler: async (response) => {
          if (response.headers.get('Content-Type')?.includes('text/csv')) {
            // Handle CSV response
            return response.text();
          } else if (response.headers.get('Content-Type')?.includes('application/pdf')) {
            return response.blob();
          } else {
            // Handle JSON response
            return response.json();
          }
        },
      }),
    }),
    downloadAESAEReport: builder.query({
      query: ({ sponsorId, trialId, siteId, documentKey, type }) => ({
        url: `/e-consent/crf/report/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/documentKey/${documentKey}/type/${type}`,
        method: 'GET',
        responseHandler: async (response) => {
          if (response.headers.get('Content-Type')?.includes('text/csv')) {
            // Handle CSV response
            return response.text();
          } else if (response.headers.get('Content-Type')?.includes('application/pdf')) {
            // Handle PDF response
            const data = await response.blob();
            const blobUrl = URL.createObjectURL(data);
            return blobUrl;
          } else {
            // Handle JSON response
            return response.json();
          }
        },
      }),
    }),
    getSponsors: builder.query({
      query: () => ({
        url: 'trial-mgmt/sponsor/all',
      }),
      providesTags: ['Sponsors'],
    }),
    addSponsors: builder.mutation({
      query: ({ payload }) => ({
        url: '/trial-mgmt/sponsor',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Sponsors'],
    }),
    addSponsorFinanceDetail: builder.mutation({
      query: ({ payload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/finance-details`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Sponsors'],
    }),
    addSiteFinanceDetail: builder.mutation({
      query: ({ payload, sponsorId, siteId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/site/${siteId}/finance-details`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['SiteBasicDetail', 'SiteList'],
    }),
    addUserFinanceDetail: builder.mutation({
      query: ({ payload, userId }) => ({
        url: `/trial-mgmt/sponsor/user/${userId}/finance-details`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['SiteBasicDetail', 'SiteList'],
    }),
    addSponsorConfiguration: builder.mutation({
      query: ({ payload, sponsorId }) => ({
        url: `/trial-mgmt/dynamic-configurations/mapping/sponser/${sponsorId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['SponserConfigurationList', 'ConfigurationList'],
    }),
    getSponsorById: builder.query({
      query: (sponsorId) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}`,
      }),
      providesTags: ['Sponsors'],
    }),
    uploadOrganizationLogo: builder.mutation({
      query: ({ payload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/logo`,
        body: payload,
        method: 'POST',
      }),
      invalidatesTags: ['Sponsors'],
    }),
    addSponsorAdmin: builder.mutation({
      query: ({ payload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/sponsor-admin-user`,
        body: payload,
        method: 'POST',
      }),
      invalidatesTags: ['Sponsors'],
    }),
    getSponsorAdminById: builder.query({
      query: (sponsorId) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/sponsor-admin-user`,
      }),
      providesTags: ['Sponsors'],
    }),
    addFianceAdmin: builder.mutation({
      query: ({ payload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/finance-admin-user`,
        body: payload,
        method: 'POST',
      }),
    }),
    addDataManager: builder.mutation({
      query: ({ payload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/data-manager-user`,
        body: payload,
        method: 'POST',
      }),
      invalidatesTags: ['Sponsors'],
    }),
    getFianceAdminById: builder.query({
      query: (sponsorId) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/finance-admin-user`,
      }),
      providesTags: ['Sponsors'],
    }),
    getDataManagerById: builder.query({
      query: (sponsorId) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/data-manager-user`,
      }),
      providesTags: ['Sponsors'],
    }),
    // Sponsor Level Users API (Data Manager & Site Monitor)
    addSponsorLevelUser: builder.mutation({
      query: ({ payload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/sponsor-level-user`,
        body: payload,
        method: 'POST',
      }),
      invalidatesTags: ['Sponsors', 'SponsorLevelUsers'],
    }),
    getSponsorLevelUsers: builder.query({
      query: ({ sponsorId, userType }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/sponsor-level-user${userType ? `?userType=${userType}` : ''}`,
      }),
      providesTags: ['Sponsors', 'SponsorLevelUsers'],
    }),
    getAllConfigurations: builder.query({
      query: () => ({
        url: '/trial-mgmt/dynamic-configurations/configuration-labels',
      }),
      providesTags: ['ConfigurationList'],
    }),
    getAllSponserConfigurations: builder.query({
      query: (sponsorId) => ({
        url: `/trial-mgmt/dynamic-configurations/mapping/sponser/${sponsorId}`,
      }),
      providesTags: ['SponserConfigurationList'],
    }),
    addConfiguration: builder.mutation({
      query: (newConfiguration) => ({
        url: `/trial-mgmt/dynamic-configurations/configuration`,
        method: 'POST',
        body: newConfiguration,
      }),
      invalidatesTags: ['ConfigurationList'],
    }),
    deleteConfigurationOption: builder.mutation({
      query: (optionId) => ({
        url: `/trial-mgmt/dynamic-configurations/option/${optionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ConfigurationList'],
    }),
    updateConfigurationOption: builder.mutation({
      query: ({ optionId, payload }) => ({
        url: `/trial-mgmt/dynamic-configurations/option/${optionId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['ConfigurationList'],
    }),
    deleteConfigurationTable: builder.mutation({
      query: (labelId) => ({
        url: `/trial-mgmt/dynamic-configurations/${labelId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ConfigurationList'],
    }),
    addTrailSys: builder.mutation({
      query: ({ payload, sponsorId }) => ({
        url: `/trial-mgmt/sponsor/sponsor/${sponsorId}/v2/trial`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Trail'],
    }),
    getTrailSys: builder.query({
      query: (sponsorId) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trials`,
      }),
      providesTags: ['Trail'],
    }),
    // SENSITIVE: Complete trial deletion - System Admin only
    deleteTrialCompletely: builder.mutation({
      query: ({ sponsorId, trialId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/trial/${trialId}/complete-delete`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Trail', 'Trial', 'TrialV2'],
    }),
    uploadConfigurationFile: builder.mutation({
      query: (formData) => ({
        url: '/form-library/library',
        method: 'POST',
        body: formData,
        formData: true, // Set to true for multipart/form-data
      }),
    }),
    updateConfigurationTable: builder.mutation({
      query: (updateConfiguration) => ({
        url: `/trial-mgmt/dynamic-configurations/configuration`,
        method: 'PATCH',
        body: updateConfiguration,
      }),
      invalidatesTags: ['ConfigurationList'],
    }),
    downloadDelegationLogs: builder.query({
      query: ({ trialId, siteId }) => ({
        url: `/trial-mgmt/doa/deligation-log/trial/${trialId}/site/${siteId}`,
        method: 'GET',
        responseHandler: async (response) => {
          if (response.headers.get('Content-Type')?.includes('text/csv')) {
            // Handle CSV response
            return response.text();
          } else if (response.headers.get('Content-Type')?.includes('application/pdf')) {
            // Handle PDF response
            const data = await response.blob();
            const blobUrl = URL.createObjectURL(data);
            return blobUrl;
          } else {
            // Handle JSON response
            return response.json();
          }
        },
      }),
    }),
    downloadVisitCsv: builder.mutation({
      query: ({ sponsorId, trialId, siteId }) => ({
        url: `/e-consent/crf/report/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}`,
        method: 'GET',
        responseHandler: async (response) => {
          if (response.headers.get('Content-Type')?.includes('text/csv')) {
            // Handle CSV response
            return response.text();
          } else if (response.headers.get('Content-Type')?.includes('application/pdf')) {
            return response.blob();
          } else {
            // Handle JSON response
            return response.json();
          }
        },
      }),
    }),

    exportSubjectsReport: builder.mutation({
      query: ({ sponsorId, trialId, siteId, format, reportType }) => ({
        url: '/e-consent/crf/report/export',
        method: 'POST',
        body: {
          sponsorId,
          trialId,
          siteId,
          format,
          reportType,
        },
        responseHandler: async (response) => {
          const contentType = response.headers.get('Content-Type') || '';
          if (
            contentType.includes('text/csv') ||
            contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
          ) {
            return response.blob();
          }
          return response.json();
        },
      }),
    }),

    // Telehealth Api's
    sendMessage: builder.mutation({
      query: (payload) => ({
        url: '/trial-mgmt/messages',
        method: 'POST',
        body: payload,
      }),
    }),
    getMessageThreads: builder.query({
      query: () => ({
        url: '/trial-mgmt/messages',
        method: 'GET',
      }),
      providesTags: ['allMessages'],
    }),
    getSingleChatThread: builder.query({
      query: (sentBy) => ({
        url: `/trial-mgmt/messages/${sentBy}`,
      }),
    }),
    readMessage: builder.mutation({
      query: (payload) => ({
        url: 'trial-mgmt/messages/status',
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['allMessages'],
    }),
    getUserContacts: builder.query({
      query: () => ({
        url: `/trial-mgmt/sponsor/user/contacts`,
        method: 'GET',
      }),
      providesTags: ['UserContacts'],
    }),
    getSQSCreds: builder.query({
      query: () => ({
        url: `/trial-mgmt/telehealth/assume-role`,
        method: 'GET',
      }),
    }),
    createFinanceTrial: builder.mutation({
      query: ({ payload, actor }) => ({
        url: '/finance-service/v1/clinical-study',
        method: 'POST',
        body: payload,
        headers: { actor },
      }),
      invalidatesTags: ['financeTrials'],
    }),
    getFianceTrialById: builder.query({
      query: (studyId) => ({
        url: `/finance-service/v1/clinical-study?studyId=${studyId}`,
        method: 'GET',
      }),
      providesTags: ['financeTrial'],
    }),
    getFinanceTrials: builder.query({
      query: ({ sponsorEntityId, skipStatus, page, limit }) => ({
        url: `/finance-service/v1/clinical-study/sponsor/${sponsorEntityId}`,
        method: 'GET',
        params: {
          skipStatus,
          page,
          limit,
        },
      }),
      providesTags: ['financeTrials'],
    }),
    getFianceEnteties: builder.mutation({
      query: (body) => ({
        url: `/finance-service/v1/entity/fetch-multiple`,
        method: 'POST',
        body,
      }),
      providesTags: ['entitites'],
    }),
    getOnboardedEntities: builder.query({
      query: (params) => ({
        url: `/finance-service/v1/entity/list`,
        method: 'GET',
        params,
      }),
      providesTags: ['entitites'],
    }),
    getEntityTypes: builder.query({
      query: () => ({
        url: `/finance-service/v1/entity/types`,
        method: 'GET',
      }),
      providesTags: ['entitites'],
    }),
    assignFinanceSiteStudy: builder.mutation({
      query: ({ siteEntityId, studyId, totalSubjectNumber, revenuePercentage, actor }) => ({
        url: `/finance-service/v1/clinical-study/site/${siteEntityId}?studyId=${studyId}`,
        method: 'POST',
        body: {
          totalSubjectNumber: totalSubjectNumber || 0,
          revenuePercentage: revenuePercentage || 0,
        },
        headers: { actor },
      }),
      invalidatesTags: ['financeTrial'],
    }),
    deleteFinanceSiteStudy: builder.mutation({
      query: ({ siteEntityId, studyId, actor }) => ({
        url: `/finance-service/v1/clinical-study/${studyId}/site/${siteEntityId}`,
        method: 'DELETE',
        headers: { actor },
      }),
      invalidatesTags: ['financeTrial'],
    }),
    uploadAccountCsv: builder.mutation({
      query: ({ body, actor }) => ({
        url: `/finance-service/v1/entity/upload`,
        method: 'POST',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['entitites'],
    }),
    uploadTriggerPaymentCsv: builder.mutation({
      query: ({ protocolNumber, siteEntityId, body, actor }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}/site/${siteEntityId}/event/upload`,
        method: 'POST',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['entitites', 'siteTransactions'],
    }),
    triggerFinanceEvent: builder.mutation({
      query: ({ protocolNumber, siteEntityId, eventKey, subjectEmail, actor }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}/site/${siteEntityId}/event/${eventKey}/trigger`,
        method: 'POST',
        body: subjectEmail ? { subjectEmail } : {},
        headers: { actor },
      }),
      invalidatesTags: ['siteTransactions', 'financeTrialSite'],
    }),
    downloadCsvTemplate: builder.mutation({
      query: (type) => ({
        url: `/finance-service/v1/clinical-study/download-template?type=${type}`,
        method: 'POST',
        responseHandler: async (response) => {
          if (response.headers.get('Content-Type')?.includes('text/csv')) {
            // Handle CSV response
            return response.text();
          } else if (response.headers.get('Content-Type')?.includes('application/pdf')) {
            return response.blob();
          } else {
            // Handle JSON response
            return response.json();
          }
        },
      }),
    }),
    uploadBudgetCsv: builder.mutation({
      query: ({ protocolNumber, siteEntityId, body, actor }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}/site/${siteEntityId}/upload`,
        method: 'POST',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['financeTrial', 'siteTransactions'],
    }),
    updateFinanceTrialStatus: builder.mutation({
      query: ({ protocolNumber, body, actor }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}`,
        method: 'PATCH',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['financeTrial', 'financeTrials'],
    }),
    updateEventPayoutAmendment: builder.mutation({
      query: ({ protocolNumber, siteEntityId, eventId, payoutId, body, actor }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}/site/${siteEntityId}/event/${eventId}/payout/${payoutId}/amendment`,
        method: 'PATCH',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['financeTrialSite, siteTransactions'],
    }),
    updateEventAmendment: builder.mutation({
      query: ({ protocolNumber, siteEntityId, eventId, body, actor }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}/site/${siteEntityId}/event/${eventId}/amendment`,
        method: 'PATCH',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['financeTrialSite, siteTransactions'],
    }),
    getFinanceSiteDetails: builder.query({
      query: ({ trialId, siteId }) => ({
        url: `/finance-service/v1/clinical-study/site/${siteId}?studyId=${trialId}`,
      }),
      providesTags: ['financeTrialSite'],
    }),
    getSiteTransactions: builder.query({
      query: ({ params }) => ({
        url: `/finance-service/v1/invoice/transactions`,
        params,
      }),
      providesTags: ['siteTransactions'],
    }),
    downloadSiteTransactions: builder.mutation({
      query: ({ query }) => ({
        url: `/finance-service/v1/invoice/download-transactions` + query,
        method: 'GET',
        responseHandler: async (response) => {
          if (response.headers.get('Content-Type')?.includes('text/csv')) {
            // Handle CSV response
            return response.text();
          } else if (response.headers.get('Content-Type')?.includes('application/pdf')) {
            return response.blob();
          } else {
            // Handle JSON response
            return response.json();
          }
        },
      }),
    }),
    addFinanceEntity: builder.mutation({
      query: ({ body, actor }) => ({
        url: `/finance-service/v1/entity/onboard`,
        method: 'POST',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['entitites', 'financeTrialSite', 'siteSubjects'],
    }),
    assignSubjectToStudySite: builder.mutation({
      query: ({ protocolNumber, siteEntityId, subjectEmail, actor }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}/site/${siteEntityId}/subject/${encodeURIComponent(subjectEmail)}`,
        method: 'POST',
        headers: { actor },
      }),
      invalidatesTags: ['financeTrialSite', 'entitites', 'siteSubjects'],
    }),
    getSiteSubjects: builder.query({
      query: ({ protocolNumber, siteEntityId, studySiteMappingId, page = 1, limit = 10, search }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}/site/${siteEntityId}/subjects`,
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(studySiteMappingId ? { studySiteMappingId } : {}),
        },
      }),
      providesTags: (result, error, arg) => [
        {
          type: 'siteSubjects',
          id: `${arg.protocolNumber}-${arg.studySiteMappingId ?? arg.siteEntityId}`,
        },
      ],
    }),
    uploadSubjectOnboardingCsv: builder.mutation({
      query: ({ protocolNumber, siteEntityId, body, actor }) => ({
        url: `/finance-service/v1/clinical-study/${protocolNumber}/site/${siteEntityId}/subjects/upload`,
        method: 'POST',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['siteSubjects', 'financeTrialSite', 'entitites'],
    }),
    generateFianceInvoice: builder.mutation({
      query: ({ body, actor }) => ({
        url: `/finance-service/v1/invoice`,
        method: 'POST',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['invoices', 'siteTransactions'],
    }),
    amendInvoice: builder.mutation({
      query: ({ id, body, actor }) => ({
        url: `/finance-service/v1/invoice/${id}/amend`,
        method: 'PATCH',
        body: body,
        headers: { actor },
      }),
      invalidatesTags: ['invoices', 'siteTransactions'],
    }),
    getPendingInvoiceAmendments: builder.query({
      query: ({ recipientEntityId }) => ({
        url: `/finance-service/v1/invoice/pending-amendments`,
        method: 'GET',
        params: { recipientEntityId },
      }),
      providesTags: ['invoices'],
    }),
    getSiteInvoices: builder.query({
      query: ({ params }) => ({
        url: `/finance-service/v1/invoice`,
        params,
      }),
      providesTags: ['invoices'],
    }),
    getInvoice: builder.query({
      query: ({ id }) => ({
        url: `/finance-service/v1/invoice/${id}`,
        method: 'GET',
      }),
    }),
    downloadInvoiceSignature: builder.query({
      query: ({ invoiceId }) => ({
        url: `/finance-service/v1/invoice/${invoiceId}/download-signature`,
        method: 'GET',
      }),
    }),
    getDashboardConfigMaster: builder.query({
      query: () => ({
        url: `/trial-mgmt/v1/dashboard/preferences-master`,
        method: 'GET',
      }),
    }),
    applyDashboardPreferences: builder.mutation({
      query: (body) => ({
        url: '/trial-mgmt/v1/dashboard/preferences',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        arg && arg?.userId ? [{ type: 'userPreferences', id: arg?.userId }] : [],
    }),
    approveTransactions: builder.mutation({
      query: ({ payload, actor }) => ({
        url: '/finance-service/v1/invoice/transactions/approve',
        method: 'POST',
        body: payload,
        headers: { actor },
      }),
    }),
    requestVerification: builder.mutation({
      query: (accId) => ({
        url: `/finance-service/v1/entity/${accId}/request-verification`,
        method: 'POST',
      }),
    }),

    updateSponsorDetails: builder.mutation({
      query: ({ sponsorId, body }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: ['Sponsors'],
    }),
    getSiteById: builder.query({
      query: ({ sponsorId, siteId }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/site/${siteId}`,
      }),
      providesTags: (result, error, arg) => [{ type: 'SiteBasicDetail', id: arg.siteId }],
    }),
    updateSiteDetails: builder.mutation({
      query: ({ sponsorId, siteId, body }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/site/${siteId}`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: ['SiteBasicDetail'],
    }),
    updateUserDetails: builder.mutation({
      query: ({ sponsorId, userId, body }) => ({
        url: `/trial-mgmt/sponsor/${sponsorId}/user/${userId}`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: [(result, error, arg) => [{ type: 'userBasicDetails', id: arg?.userId }]],
    }),
    resetUserTempPassword: builder.mutation({
      query: ({ email }) => ({
        url: `/trial-mgmt/password/forget-password/${email}`,
        method: 'PUT',
      }),
    }),
    systemAdminResetPassword: builder.mutation({
      query: ({ userId, newPassword }) => ({
        url: `/trial-mgmt/sponsor/system-admin/user/${userId}/reset-password`,
        method: 'PUT',
        body: { newPassword },
      }),
    }),
    // System Admin - Update User Details and Preferences
    systemAdminUpdateUser: builder.mutation({
      query: ({ userId, body }) => ({
        url: `/trial-mgmt/sponsor/system-admin/user/${userId}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'userBasicDetails', id: arg?.userId }],
    }),
    // System Admin - Get User Details
    systemAdminGetUserDetails: builder.query({
      query: ({ userId }) => ({
        url: `/trial-mgmt/sponsor/system-admin/user/${userId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => [{ type: 'userBasicDetails', id: arg?.userId }],
    }),
    getUserPreferences: builder.query({
      query: ({ userId }) => ({
        url: `/trial-mgmt/v1/dashboard/preferences/user/${userId}`,
      }),
      providesTags: (result, error, arg) => [{ type: 'userPreferences', id: arg.userId }],
    }),

    getSubjectTempParticipantId: builder.query({
      query: ({ sponsorId, trialId, siteId }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/temp-participant-id`,
        method: 'GET',
      }),
    }),

    getSubjectCountByField: builder.query({
      query: ({ sponsorId, trialId, siteId, body }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/site/${siteId}/field-aggregations`,
        method: 'POST',
        body: body,
      }),
    }),

    getMultiSiteSubjectCountByField: builder.query({
      query: ({ sponsorId, trialId, body }) => ({
        url: `/e-consent/subject/sponsor/${sponsorId}/trial/${trialId}/multi-site-field-aggregations`,
        method: 'POST',
        body: body,
      }),
    }),

    getBalancingCriteriaConfig: builder.query({
      query: ({ type, documentKey }) => ({
        url: `/e-consent/crf/report-config-master/${type}/${documentKey}`,
        method: 'GET',
      }),
    }),

    // ==========================================
    // FAQ MODULE APIs
    // ==========================================

    // Admin - FAQ Categories
    createFaqCategory: builder.mutation({
      query: (payload) => ({
        url: '/trial-mgmt/admin/faq-categories',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['FaqCategories', 'PublicFaqs'],
    }),
    getFaqCategories: builder.query({
      query: ({ includeInactive = true } = {}) => ({
        url: `/trial-mgmt/admin/faq-categories?includeInactive=${includeInactive}`,
      }),
      providesTags: ['FaqCategories'],
    }),
    getFaqCategoryById: builder.query({
      query: (id) => ({
        url: `/trial-mgmt/admin/faq-categories/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: 'FaqCategories', id }],
    }),
    updateFaqCategory: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/trial-mgmt/admin/faq-categories/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['FaqCategories', 'PublicFaqs'],
    }),
    updateFaqCategoryStatus: builder.mutation({
      query: ({ id, isActive }) => ({
        url: `/trial-mgmt/admin/faq-categories/${id}/status`,
        method: 'PATCH',
        body: { isActive },
      }),
      invalidatesTags: ['FaqCategories', 'PublicFaqs'],
    }),
    deleteFaqCategory: builder.mutation({
      query: (id) => ({
        url: `/trial-mgmt/admin/faq-categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FaqCategories', 'Faqs', 'PublicFaqs'],
    }),

    // Admin - FAQs
    createFaq: builder.mutation({
      query: (payload) => ({
        url: '/trial-mgmt/admin/faqs',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Faqs', 'FaqCategories', 'PublicFaqs'],
    }),
    getFaqs: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value);
          }
        });
        return {
          url: `/trial-mgmt/admin/faqs?${queryParams.toString()}`,
        };
      },
      providesTags: ['Faqs'],
    }),
    getFaqById: builder.query({
      query: (id) => ({
        url: `/trial-mgmt/admin/faqs/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: 'Faqs', id }],
    }),
    updateFaq: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/trial-mgmt/admin/faqs/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['Faqs', 'FaqCategories', 'PublicFaqs'],
    }),
    deleteFaq: builder.mutation({
      query: (id) => ({
        url: `/trial-mgmt/admin/faqs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Faqs', 'FaqCategories', 'PublicFaqs'],
    }),

    // Admin - FAQ Import/Export
    exportFaqs: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value);
          }
        });
        return {
          url: `/trial-mgmt/admin/faqs/export?${queryParams.toString()}`,
        };
      },
    }),
    importFaqs: builder.mutation({
      query: (payload) => ({
        url: '/trial-mgmt/admin/faqs/import',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Faqs', 'FaqCategories', 'PublicFaqs'],
    }),
    validateFaqImport: builder.mutation({
      query: (payload) => ({
        url: '/trial-mgmt/admin/faqs/import/validate',
        method: 'POST',
        body: payload,
      }),
    }),
    getFaqImportTemplate: builder.query({
      query: () => ({
        url: '/trial-mgmt/admin/faqs/import/template',
      }),
    }),

    // Public - FAQs (for end users)
    getPublicFaqs: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value);
          }
        });
        return {
          url: `/trial-mgmt/faqs?${queryParams.toString()}`,
        };
      },
      providesTags: ['PublicFaqs'],
    }),
    getPublicFaqCategories: builder.query({
      query: () => ({
        url: '/trial-mgmt/faqs/categories',
      }),
      providesTags: ['PublicFaqs'],
    }),
    getPublicFaqsByCategory: builder.query({
      query: (categoryId) => ({
        url: `/trial-mgmt/faqs/categories/${categoryId}/faqs`,
      }),
      providesTags: (result, error, categoryId) => [{ type: 'PublicFaqs', id: categoryId }],
    }),

    // Support Tickets - Public create (no auth)
    createSupportTicket: builder.mutation({
      query: (body) => ({
        url: '/trial-mgmt/support-tickets',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MySupportTickets'],
    }),
    // Support Tickets - User "my tickets" (auth required)
    getMySupportTickets: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page != null) queryParams.append('page', params.page);
        if (params.limit != null) queryParams.append('limit', params.limit);
        const q = queryParams.toString();
        return { url: `/trial-mgmt/support-tickets/me${q ? `?${q}` : ''}` };
      },
      providesTags: ['MySupportTickets'],
    }),
    getMySupportTicketById: builder.query({
      query: (id) => ({ url: `/trial-mgmt/support-tickets/me/${id}` }),
      providesTags: (result, error, id) => [{ type: 'MySupportTicketDetail', id }],
    }),
    // Support Tickets - Admin (auth + system_admin)
    getSupportTickets: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') queryParams.append(key, value);
        });
        return { url: `/trial-mgmt/admin/support-tickets?${queryParams.toString()}` };
      },
      providesTags: ['SupportTickets'],
    }),
    getSupportTicketById: builder.query({
      query: (id) => ({ url: `/trial-mgmt/admin/support-tickets/${id}` }),
      providesTags: (result, error, id) => [{ type: 'SupportTicketDetail', id }],
    }),
    addSupportTicketReply: builder.mutation({
      query: ({ id, message }) => ({
        url: `/trial-mgmt/admin/support-tickets/${id}/replies`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (result, error, { id }) => ['SupportTickets', { type: 'SupportTicketDetail', id }],
    }),
    updateSupportTicketStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/trial-mgmt/admin/support-tickets/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => ['SupportTickets', { type: 'SupportTicketDetail', id }],
    }),
  }),
});

export const {
  useLoginMutation,
  useVerifyAuthOTPMutation,
  useResendMfaOtpMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useForgetPasswordMutation,
  useResetPasswordMutation,
  useSetProfileMutation,
  useVerifyOTPMutation,
  useResendOTPMutation,
  useBookVisitSlotMutation,
  useCreateTrialMutation,
  useUpdateTrialBasicDetailsMutation,
  useUploadDocMutation,
  useCreateTrialPatchMutation,
  useGetTrialsMutation,
  useGetSiteMembersQuery,
  useGetTrialSiteDetailsQuery,
  useGetTrialVisitDetailsQuery,
  useGetDownloadTrialDocQuery,
  useGetTrialSiteMembersQuery,
  useUploadSignatureMutation,
  useAssignRoleMutation,
  useAddSiteMemberMutation,
  useDeleteAssignedTaskMutation,
  useDeleteTrialDocumentsMutation,
  useDeleteUserRoleMutation,
  useGetTrialDetailsQuery,
  useUploadSubjectFileMutation,
  useUpdateAssignedTaskMutation,
  useBulkVerifyDOATasksMutation,
  useDownloadSubjectFileQuery,
  useDownloadDOASignatureQuery,
  useGetSubjectDashboardDetailQuery,
  usePlayConsentAudioMutation,
  useAddDOACommentMutation,
  useDownloadSubjectSignatureQuery,
  useGetTrialDocTypesQuery,
  useDownloadVisitSignatureQuery,
  useGetTrialDocumentsQuery,
  useFetchUserAssigngedTasksQuery,
  useGetSiteBasicDetailsQuery,
  useDownloadCRFFileQuery,
  useCreateTrialSitePatchMutation,
  useUpdateTrialBankDetailMutation,
  useUpdateTrialStudyDetailMutation,
  useUpdateTrialPatchMutation,
  useUpdateTrialBudgetDetailMutation,
  useGetDynamicDataValuesQuery,
  useGetSiteDocumentMetaQuery,
  useGetSiteDocumentsQuery,
  useMarkTrialSetupDoneMutation,
  useGetOngoingTrialsQuery,
  useGetDepartmentMetaQuery,
  useCreateSiteMutation,
  useGetVisitAnswersQuery,
  useGetTrialSiteInfoQuery,
  useGetLibraryFormQuery,
  useGetJsonFormQuery,
  useUploadCRFFileMutation,
  useGetCommentsQuery,
  useGetFieldHistoryQuery,
  useChangeDOATaskStatusMutation,
  useGetSubjectFieldHistoryQuery,
  useSaveDOASignMutation,
  useGenerateOTPValueMutation,
  useVerifyOTPValueMutation,
  useSaveCommentMutation,
  useUploadVisitSignatureMutation,
  useSubmitSiteInitAnswersMutation,
  useGetTrialSiteAnswersQuery,
  useUpdateSiteInitAnswersMutation,
  useVerifySiteInitAnswersMutation,
  useSaveVisitAnswersMutation,
  useGetSponsorDetailsQuery,
  useGetSponsorLogoQuery,
  useSaveSubjectEnrollInfoMutation,
  useGetSubjectListQuery,
  useGetSubjectAnswersQuery,
  useUploadSubjectSignatureMutation,
  useDownloadSubjectProfilePicQuery,
  useUploadSubjectProfilePicMutation,
  useGetSubjectDetailQuery,
  useAddFieldSubjectEnrollmentMutation,
  useGetEConsentFormConfigQuery,
  useUploadInitiationFileMutation,
  useDownloadInitiationFileQuery,
  useExternalVerifySubjectMutation,
  useExternalVerifyCRFMutation,
  useResendOTPValueMutation,
  useAddDepartmentInMetaMutation,
  useSaveWithdrawSubAnswersMutation,
  useGetWithdrawSubjectListQuery,
  useGetOnHoldSubjectListQuery,
  useReEnrollSubjectMutation,
  useGetSubjectListByFieldMutation,
  useCreateScheduleMutation,
  useGetScheduleMutation,
  useAddCommentOnScheduleEventMutation,
  useGetOneScheduleQuery,
  useGetMeUserQuery,
  useUploadUserProfilePicMutation,
  useDownloadUserProfilePicQuery,
  useGetSignaturePreferencesQuery,
  useUpdateSignaturePreferencesMutation,
  useSaveUserSignatureMutation,
  useGetUserSavedSignatureQuery,
  useDeleteUserSignatureMutation,
  useLazyDownloadSignatureQuery,
  useUpdateScheduleMutation,
  useGetSiteInitiationConfigQuery,
  useAddFieldSiteInitiationMutation,
  useRemoveFieldSubjectEnrollmentMutation,
  useRemoveFieldSiteInitiationMutation,
  useCreateTrialBookingSlotMutation,
  useGetTrialBookingSlotsQuery,
  useDeleteTrialBookingMutation,
  useUpdateTrialBookingSlotMutation,
  usePublishBookingSlotMutation,
  useDashboradDataQuery,
  useFinanceDashboradDataQuery,
  useUpdateDashboardPreferenceMutation,
  useCancelVisitSlotMutation,
  useGetBookedTrialsSlotsQuery,
  useFetchDAOQuery,
  useFetchAllDAOTasksQuery,
  useDeleteDOADocumentsMutation,
  useCreateDoaTaskMutation,
  useDeleteDoaTaskMutation,
  useUpdateTaskMutation,
  useAssignTaskMutation,
  useCancelScheduledEventMutation,
  useUploadDoaTaskDocumentMutation,
  useDownloadAuditFileMutation,
  useDownloadFinanceAuditFileMutation,
  useDownloadAESAEReportQuery,
  useGetSponsorsQuery,
  useAddSponsorsMutation,
  useAddSponsorFinanceDetailMutation,
  useAddSiteFinanceDetailMutation,
  useAddUserFinanceDetailMutation,
  useGetFinanceTrialsQuery,
  useAddSponsorConfigurationMutation,
  useGetSponsorByIdQuery,
  useUploadOrganizationLogoMutation,
  useAddSponsorAdminMutation,
  useGetSponsorAdminByIdQuery,
  useAddConfigurationMutation,
  useGetAllConfigurationsQuery,
  useGetAllSponserConfigurationsQuery,
  useDeleteConfigurationOptionMutation,
  useUpdateConfigurationOptionMutation,
  useDeleteConfigurationTableMutation,
  useUpdateConfigurationTableMutation,
  useLazyDownloadDelegationLogsQuery,
  useAddTrailSysMutation,
  useGetTrailSysQuery,
  useDeleteTrialCompletelyMutation,
  useUploadConfigurationFileMutation,
  useDownloadVisitCsvMutation,
  useSendMessageMutation,
  useGetMessageThreadsQuery,
  useGetSingleChatThreadQuery,
  useReadMessageMutation,
  useGetUserContactsQuery,
  useGetSQSCredsQuery,
  useCreateFinanceTrialMutation,
  useGetFianceTrialByIdQuery,
  useGetFianceEntetiesMutation,
  useGetOnboardedEntitiesQuery,
  useGetEntityTypesQuery,
  useUploadAccountCsvMutation,
  useUploadBudgetCsvMutation,
  useAssignFinanceSiteStudyMutation,
  useUpdateFinanceTrialStatusMutation,
  useDownloadCsvTemplateMutation,
  useGetFinanceSiteDetailsQuery,
  useGetSiteTransactionsQuery,
  useUploadTriggerPaymentCsvMutation,
  useTriggerFinanceEventMutation,
  useDownloadSiteTransactionsMutation,
  useAddFinanceEntityMutation,
  useAssignSubjectToStudySiteMutation,
  useGetSiteSubjectsQuery,
  useUploadSubjectOnboardingCsvMutation,
  useGenerateFianceInvoiceMutation,
  useGetSiteInvoicesQuery,
  useDeleteFinanceSiteStudyMutation,
  useUpdateEventPayoutAmendmentMutation,
  useUpdateEventAmendmentMutation,
  useGetInvoiceQuery,
  useAmendInvoiceMutation,
  useGetPendingInvoiceAmendmentsQuery,
  useLazyGetInvoiceQuery,
  useLazyDownloadInvoiceSignatureQuery,
  useAddFianceAdminMutation,
  useGetFianceAdminByIdQuery,
  useAddDataManagerMutation,
  useGetDataManagerByIdQuery,
  useAddSponsorLevelUserMutation,
  useGetSponsorLevelUsersQuery,
  useApproveTransactionsMutation,
  useRequestVerificationMutation,
  useGetDashboardConfigMasterQuery,
  useApplyDashboardPreferencesMutation,
  useUpdateSponsorDetailsMutation,
  useGetSiteByIdQuery,
  useUpdateSiteDetailsMutation,
  useUpdateUserDetailsMutation,
  useResetUserTempPasswordMutation,
  useSystemAdminResetPasswordMutation,
  useSystemAdminUpdateUserMutation,
  useSystemAdminGetUserDetailsQuery,
  useLazySystemAdminGetUserDetailsQuery,
  useGetUserPreferencesQuery,

  // v2 api's
  useGetTrialDetailsV2Query,
  useCreateTrialV2Mutation,
  useCreateTrialPatchV2Mutation,
  useGetTrialSiteDetailsV2Query,
  useCreateTrialSitePatchV2Mutation,
  useMarkTrialSetupDoneV2Mutation,
  useGetSubjectTempParticipantIdQuery,
  useExportSubjectsReportMutation,
  useGetSubjectCountByFieldQuery,
  useGetBalancingCriteriaConfigQuery,

  // FAQ Module hooks
  useCreateFaqCategoryMutation,
  useGetFaqCategoriesQuery,
  useGetFaqCategoryByIdQuery,
  useUpdateFaqCategoryMutation,
  useUpdateFaqCategoryStatusMutation,
  useDeleteFaqCategoryMutation,
  useCreateFaqMutation,
  useGetFaqsQuery,
  useGetFaqByIdQuery,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useGetPublicFaqsQuery,
  useGetPublicFaqCategoriesQuery,
  useGetPublicFaqsByCategoryQuery,
  // FAQ Import/Export hooks
  useLazyExportFaqsQuery,
  useImportFaqsMutation,
  useValidateFaqImportMutation,
  useLazyGetFaqImportTemplateQuery,

  useCreateSupportTicketMutation,
  useGetMySupportTicketsQuery,
  useGetMySupportTicketByIdQuery,
  useGetSupportTicketsQuery,
  useGetSupportTicketByIdQuery,
  useAddSupportTicketReplyMutation,
  useUpdateSupportTicketStatusMutation,
} = bloqcibeApi;
